import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { RemoteServiceStore, RemoteServiceThresholdConfiguration } from '@store';
import { RemoteServiceService } from '../../remote-service.service';

import { REMOTE_SERVICE_UNITS_ADAPTER_IT } from '../../../../../operational-data-common/services/units-adapter/units-adapter.module';
import { UnitsAdapter } from '../../../../../operational-data-common/services/units-adapter/units.adapter';
import { RemoteServiceUnitType } from '../../../../../operational-data-common/services/units-adapter/units.model';
import { sanitizeValue } from '../remote-service-thresholds.helpers';

@Component({
  selector: 'app-remote-service-thresholds-tile',
  templateUrl: './remote-service-thresholds-tile.component.html',
  styleUrls: ['./remote-service-thresholds-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemoteServiceThresholdsTileComponent implements OnInit, OnChanges, OnDestroy {
  @Input() threshold: RemoteServiceThresholdConfiguration;
  standardUnitsThreshold: RemoteServiceThresholdConfiguration;

  thresholdForm = new FormControl<string>(null, []);

  pendingChanges = false;
  tileFocused = false;
  ngUnsubscribe$: Subject<void> = new Subject<void>();

  tileSaving = false;
  blockSaving = false;

  latestSavedValue: number;

  maxInputValueEntered = new Subject<string>();

  maxlengthForInput: number;

  constructor(
    @Inject(REMOTE_SERVICE_UNITS_ADAPTER_IT) public unitsAdapter: UnitsAdapter,
    public remoteServiceStore: RemoteServiceStore,
    public remoteServiceService: RemoteServiceService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  @HostListener('document:click', ['$event.target'])
  public onPageClick(targetElement): void {
    const clickedOnTile = this.elementRef.nativeElement.contains(targetElement);
    if (clickedOnTile) {
      this.tileFocused = true;
    } else {
      this.tileFocused = false;
    }
  }

  ngOnChanges(): void {
    this.standardUnitsThreshold = {
      ...this.threshold,
      unit: this.unitsAdapter.getUnit(this.threshold.unit),
      max: this.unitsAdapter.getValue(this.threshold.max, this.threshold.unit),
      min: this.unitsAdapter.getValue(this.threshold.min, this.threshold.unit),
      default: this.unitsAdapter.getValue(this.threshold.default, this.threshold.unit),
      value: this.unitsAdapter.getValue(this.threshold.value, this.threshold.unit),
    };
  }

  ngOnInit(): void {
    this.thresholdForm.setValue(this.standardUnitsThreshold.value.toString());
    this.latestSavedValue = this.standardUnitsThreshold.value;
    this.initMaxlengthForInput();

    this.remoteServiceStore.thresholdsStatus$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((status) => {
        this.tileSaving = status.savingThreshold === this.standardUnitsThreshold.name;
        this.cdr.markForCheck();
      });

    this.maxInputValueEntered
      .pipe(debounceTime(1000), takeUntil(this.ngUnsubscribe$))
      .subscribe((inputValue) => {
        this.setMaxInputValue(inputValue);
      });
  }

  sanitizeInput(value: string): void {
    const startWithDot = value.startsWith('.');
    if (startWithDot) {
      value = `0.${value}`;
    }

    const sanitizedValue = sanitizeValue(value, RemoteServiceUnitType.Bar);
    this.thresholdForm.setValue(sanitizedValue);
    this.pendingChanges = true;

    this.blockSaving = true;
    this.maxInputValueEntered.next(sanitizedValue);
  }

  private setMaxInputValue(value): void {
    if (+value > this.standardUnitsThreshold.max) {
      this.thresholdForm.setValue(this.standardUnitsThreshold.max.toString());
    } else if (+value < this.standardUnitsThreshold.min) {
      this.thresholdForm.setValue(this.standardUnitsThreshold.min.toString());
    }
    this.blockSaving = false;
    this.cdr.markForCheck();
  }

  cancelChanges(): void {
    this.thresholdForm.setValue(this.latestSavedValue.toString());
    this.pendingChanges = false;
    this.tileFocused = false;
  }

  resetToDefault(): void {
    this.latestSavedValue = +this.thresholdForm.value;
    this.thresholdForm.setValue(this.standardUnitsThreshold.default.toString());
    this.remoteServiceStore.saveThreshold(
      this.standardUnitsThreshold.group,
      this.standardUnitsThreshold.name,
      +this.thresholdForm.value
    );
    this.pendingChanges = false;
    this.tileFocused = false;
  }

  saveChanges(): void {
    this.latestSavedValue = +this.thresholdForm.value;

    let valueInMetric = this.unitsAdapter.getMetricValue(
      this.latestSavedValue,
      this.standardUnitsThreshold.unit
    );

    if (valueInMetric > this.threshold.max) {
      valueInMetric = this.threshold.max;
    } else if (valueInMetric < this.threshold.min) {
      valueInMetric = this.threshold.min;
    }

    this.remoteServiceStore.saveThreshold(
      this.standardUnitsThreshold.group,
      this.standardUnitsThreshold.name,
      valueInMetric
    );

    this.pendingChanges = false;
    this.tileFocused = false;
  }

  private initMaxlengthForInput(): void {
    const minLength = 3;
    const maxlengthOfThresholdData = Math.max(
      this.standardUnitsThreshold.default.toString().length,
      this.standardUnitsThreshold.min.toString().length,
      this.standardUnitsThreshold.max.toString().length
    );

    this.maxlengthForInput =
      maxlengthOfThresholdData > minLength ? maxlengthOfThresholdData : minLength;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
